import React, { ComponentType, FC, ReactNode, useId } from 'react';
import { Controller, SubmitHandler, UseFormReturn } from 'react-hook-form';
import clsx from 'clsx';
import Button from '../../atoms/Button';
import Modal from '../../atoms/Modal';
import HookFormProvider from '../../controls/HookFormProvider';
import './ModalContainer.scss';
import Hint from '../../atoms/Hint';
import type { ModalSize } from '../../types/ModalSize';
import FormProviderControl, { type IFormProviderControlProps } from '../../controls/FormProviderControl';
import ModalLoadingOverlay from '../../atoms/ModalLoadingOverlay';
import { useUnsavedFormInput } from '../../hooks/useUnsavedFormInput';

export interface IModalContainerProps {
    header: string;
    /** id текста вопроса в содержимом окна: диктор читает его вслед за заголовком (см. `Modal`). */
    'aria-describedby'?: string;
    size?: ModalSize;
    showOkButton: boolean;
    okButtonText?: string;
    showCancelButton: boolean;
    cancelButtonText?: string;
    onCancel: () => void;
    /**
     * Своё действие второй кнопки подвала (итог операции: «Загрузить ещё файл»); крестик, Escape и щелчок по фону
     * по-прежнему вызывают `onCancel`. Без него кнопка закрывает окно тем же путём, что крестик.
     */
    onCancelButtonClick?: () => void;
    loading?: boolean;
    /** Текст подписи под спиннером оверлея загрузки. */
    loadingLabel?: string;
    /** Прогресс загрузки (0–100). Включает индикатор прогресса в оверлее. */
    progress?: number;
    /** Подпись рядом с индикатором прогресса (напр. имя загружаемого файла). */
    progressLabel?: string;
    /**
     * Блокировать закрытие модалки (крестик, Escape, клик снаружи и «Отмена») на время операции.
     * Пока идёт сабмит формы (`formState.isSubmitting`), закрытие заблокировано и без него.
     */
    closeDisabled?: boolean;
    /**
     * Есть ли несохранённый ввод: тогда Escape и клик по фону сначала спрашивают
     * «Закрыть без сохранения?». Вызывается в момент закрытия. Без неё — правки полей формы (`useUnsavedFormInput`).
     */
    hasUnsavedInput?: () => boolean;
    errorText?: string;
    /** Жёлтое предупреждение (напр. провал валидации обязательных вложений). */
    warningText?: string;
    successText?: string;
    children?: ReactNode;
    className?: string;
    formMethods: UseFormReturn<any>;
    onSubmit: SubmitHandler<any>;
    ignoreEnterSubmit?: boolean;
    okButtonDisabled?: boolean;
    /**
     * Форма окна; приложение подставляет свою, если полям нужно окружение формы (Front — область обработчиков
     * `<changes>`, чтобы поля окна не попадали в обработчики формы под окном).
     * @default FormProviderControl пакета
     */
    formComponent?: ComponentType<IFormProviderControlProps>;
}

const ModalContainer: FC<IModalContainerProps> = ({
    size = 'm',
    okButtonText = 'ОК',
    cancelButtonText = 'Отмена',
    okButtonDisabled,
    formMethods,
    formComponent: FormComponent = FormProviderControl,
    ...props
}: IModalContainerProps) => {
    const idForm = useId();
    const childContent = React.Children.toArray(props.children);
    const hasFormContent = childContent.length > 0;
    const hasValue = (value: unknown) => (typeof value === 'string' ? value.trim().length > 0 : Boolean(value));
    const hasFeedback = [props.errorText, props.warningText, props.successText].some(hasValue);
    const hasBody = Boolean(props.loading || typeof props.progress === 'number' || hasFeedback || hasFormContent);
    const submitWithoutForm = () => {
        void formMethods.handleSubmit(props.onSubmit)();
    };
    const formInput = useUnsavedFormInput(formMethods);
    const { isSubmitting } = formMethods.formState;
    const closeDisabled = Boolean(props.closeDisabled || isSubmitting);
    const cancel = () => {
        if (!closeDisabled) props.onCancel();
    };

    const modal = (
        <Modal
            className={clsx('modal-dlg-container', props.className)}
            size={size}
            onClose={cancel}
            hasUnsavedInput={props.hasUnsavedInput ?? formInput}
            closeDisabled={closeDisabled}
            header={props.header}
            aria-describedby={props['aria-describedby']}
            footer={
                <div className="modal-dlg-buttons">
                    {props.showOkButton && (
                        <Button
                            size="sm"
                            aria-label={okButtonText}
                            type={hasFormContent ? 'submit' : 'button'}
                            form={hasFormContent ? idForm : undefined}
                            onClick={hasFormContent ? undefined : submitWithoutForm}
                            disabled={okButtonDisabled || formMethods.formState.isSubmitting || props.loading}
                        >
                            {okButtonText}
                        </Button>
                    )}
                    {props.showCancelButton && (
                        <Button
                            variant="light"
                            size="sm"
                            aria-label={cancelButtonText}
                            onClick={props.onCancelButtonClick ?? cancel}
                            disabled={closeDisabled}
                        >
                            {cancelButtonText}
                        </Button>
                    )}
                </div>
            }
        >
            {hasBody ? (
                <ModalLoadingOverlay
                    visible={!!props.loading}
                    label={props.loadingLabel}
                    progress={props.progress}
                    progressLabel={props.progressLabel}
                >
                    {/* отклик на действие — объявляется при появлении */}
                    {props.errorText && (
                        <Hint title={`Ошибка: ${props.errorText}`} tone="danger" maxWidth="100%" role="alert" />
                    )}
                    {props.warningText && (
                        <Hint title={`Внимание: ${props.warningText}`} tone="warning" maxWidth="100%" role="alert" />
                    )}
                    {props.successText && (
                        <Hint title={`${props.successText}`} tone="success" maxWidth="100%" role="alert" />
                    )}

                    {hasFormContent && (
                        <FormComponent
                            formMethods={formMethods}
                            className="modal-form"
                            onSubmit={props.onSubmit}
                            id={idForm}
                        >
                            <HookFormProvider controller={Controller}>{props.children}</HookFormProvider>
                        </FormComponent>
                    )}
                </ModalLoadingOverlay>
            ) : null}
        </Modal>
    );

    return modal;
};

export default ModalContainer;
